// Library Methods
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Utils
import {
  calculateNeighbours,
  getChartSpots,
  getPatternDevChartSpots,
  getTotalChartSpots,
} from "src/utils/exams/examReportsHelper";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import NumberGraph from "./NumberGraph/NumberGraph";
import PatternGraph from "./PatternGraph/PatternGraph";
import NumericalGraph from "./NumericalGraph/NumericalGraph";
import TotalDevGraph from "./TotalDevGraph/TotalDevGraph";
import ValueLegend from "./ValueLegend/ValueLegend";
import PatternLegend from "./PatternLegend/PatternLegend";
import PatternDevGraph from "./PatternDevGraph/PatternDevGraph";

// Constants
import { gridTypeEnums, stimulusColorEnums } from "src/constants/enums";

// Hooks
import { useComponentDimensions } from "src/hooks/window/useComponentDimensions";

// Styles
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

type VFThresholdingChartType = {
  isTerminated: boolean;
};

const VFThresholdingChart = ({ isTerminated }: VFThresholdingChartType) => {
  const { t } = useTranslation();

  const { sectionId, eyeType, examReport } = useContext(ReportSectionContext);

  const theme = useTheme();
  const smallerThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  const { clinicInfo } = useClinicContext();

  const isRedStimulus = useMemo(
    () => examReport?.exam?.stimulusColor === stimulusColorEnums.RED,
    [examReport?.exam?.stimulusColor]
  );

  const topDimensions = useComponentDimensions({
    componentId: "#top-container",
  });
  const middleDimensions = useComponentDimensions({
    componentId: "#middle-container",
  });

  const numberGraphSpots = useMemo(
    () => getChartSpots(examReport) ?? [],
    [examReport]
  );

  const patternGraphSpots = useMemo(
    () => calculateNeighbours(numberGraphSpots) ?? [],
    [numberGraphSpots]
  );

  const processedPoints = useMemo(
    () => examReport?.postProcessing?.processedPoints ?? [],
    [examReport?.postProcessing?.processedPoints]
  );

  const totalDevSpots = useMemo(
    () => getTotalChartSpots(examReport) ?? [],
    [examReport]
  );

  const patternDevSpots = useMemo(
    () => getPatternDevChartSpots(examReport) ?? [],
    [examReport]
  );

  const totalMeanDeviation = useMemo(
    () => examReport?.postProcessing?.totalMeanDeviation,
    [examReport?.postProcessing?.totalMeanDeviation]
  );

  const canDisplayTotalPatternCharts = useMemo(
    () =>
      clinicInfo?.licensing?.canDisplayTotalAndPatternDeviationNumericalCharts,
    [clinicInfo?.licensing?.canDisplayTotalAndPatternDeviationNumericalCharts]
  );

  return (
    <Box
      id={`eye-report-${sectionId}-${eyeType}`}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        rowGap: "20px",
      }}
    >
      <Box
        id="top-container"
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-evenly",
          flexDirection: { xs: "column", sm: "row" },
          rowGap: "20px",
        }}
      >
        <NumberGraph
          points={numberGraphSpots}
          eyeType={eyeType}
          gridType={examReport?.exam?.grid}
          displayTriangle={examReport?.exam?.grid !== gridTypeEnums.G_10_2.type}
          containerSize={
            smallerThanSm
              ? topDimensions?.dynamicWidth
              : (topDimensions?.dynamicWidth ?? 0) / 2.5
          }
        />
        <PatternGraph
          pressedPoints={patternGraphSpots}
          eyeType={eyeType}
          gridType={examReport?.exam?.grid}
          containerSize={
            smallerThanSm
              ? topDimensions?.dynamicWidth
              : (topDimensions?.dynamicWidth ?? 0) / 2.5
          }
        />
      </Box>

      {!isRedStimulus && (
        <Box
          id="middle-container"
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            flexDirection: { xs: "column", sm: "row" },
            rowGap: "20px",
          }}
        >
          <Grid
            container
            item
            xs={12}
            sm={10}
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: {
                xs: "center",
                sm: "space-evenly",
              },
              alignItems: "center",
              flexDirection: { xs: "column", sm: "row" },
              rowGap: "20px",
              columnGap: "30px",
            }}
          >
            {canDisplayTotalPatternCharts && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: {
                    xs: "center",
                    sm: "space-evenly",
                  },
                  alignItems: "center",
                  flexDirection: { xs: "column", sm: "row" },
                  rowGap: "20px",
                  columnGap: "30px",
                }}
              >
                <NumericalGraph
                  processedPoints={processedPoints}
                  isTerminated={isTerminated}
                  gridType={examReport?.exam?.grid}
                  isTotal={true}
                  containerSize={
                    smallerThanSm
                      ? middleDimensions?.dynamicWidth
                      : (middleDimensions?.dynamicWidth ?? 0) / 2.6
                  }
                />
                <NumericalGraph
                  processedPoints={processedPoints}
                  gridType={examReport?.exam?.grid}
                  isTerminated={isTerminated}
                  containerSize={
                    smallerThanSm
                      ? middleDimensions?.dynamicWidth
                      : (middleDimensions?.dynamicWidth ?? 0) / 2.6
                  }
                />
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Typography fontSize="14px">{t("Total Deviation")}</Typography>
              <TotalDevGraph
                pressedPoints={totalDevSpots}
                eyeType={eyeType}
                gridType={examReport?.exam?.grid}
                containerSize={
                  smallerThanSm
                    ? middleDimensions?.dynamicWidth
                    : (middleDimensions?.dynamicWidth ?? 0) / 2.6
                }
              />
            </Box>

            {totalMeanDeviation &&
            !isNaN(totalMeanDeviation) &&
            totalMeanDeviation >= -19 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Typography fontSize="14px">
                  {t("Pattern Deviation")}
                </Typography>
                <PatternDevGraph
                  pressedPoints={patternDevSpots}
                  eyeType={eyeType}
                  gridType={examReport?.exam?.grid}
                  containerSize={
                    smallerThanSm
                      ? middleDimensions?.dynamicWidth
                      : (middleDimensions?.dynamicWidth ?? 0) / 2.6
                  }
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  justifyContent: "center",
                  alignItems: "center",
                  maxWidth: smallerThanSm
                    ? middleDimensions?.dynamicWidth
                    : ((middleDimensions?.dynamicWidth ?? 0) / 2.6) * 0.9,
                }}
              >
                <Typography fontSize="14px" color="#FFF">
                  {t("Pattern Deviation")}
                </Typography>
                <Typography fontSize="14px" textAlign="center">
                  {t("MD Threshold Exceeded Limit")}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            sm={2}
            sx={{
              display: "flex",
              width: "100%",
              flexDirection: { xs: "row", sm: "column" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "center",
              gap: "20px",
            }}
          >
            <ValueLegend isTerminated={isTerminated} examReport={examReport} />
            <PatternLegend />
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default VFThresholdingChart;
